import type { FormEvent } from 'react';

import type { UploadedFile } from '@/components/files';
import { HeroChatInput } from '@/components/reasoning/HeroChatInput';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { cx } from '@/utils/cx';

interface FollowUpComposerProps {
  /**
   * Compact below `lg`, builder mode included: the same ConversationInput the
   * proposal side panel and email composer use, so there is one chat input in
   * the product rather than a hero one and a panel one that drift. The hero
   * box stays at `lg` and above — its wave footer and mb-14 overhang are what
   * make it wrong in a narrow column or on a phone.
   */
  isCompact: boolean;
  /**
   * Suppressed in builder mode: the panel beside the chat is the surface the
   * user is judging, and a centred caption under a narrow column reads as
   * chrome rather than a caveat.
   */
  showDisclaimer: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  placeholder: string;
  loading: boolean;
  error: string | null;
  orgId: number | undefined;
  uploadedFiles: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  /**
   * Resets in-flight upload tracking when the conversation changes, so an
   * upload kicked off before a switch doesn't attach itself to the new
   * conversation's draft. Pass the conversation id.
   */
  uploadContextKey?: string | number;
}

/** The follow-up message composer under the thread, plus its AI caveat. */
export function FollowUpComposer({
  isCompact,
  showDisclaimer,
  ...inputProps
}: FollowUpComposerProps) {
  return (
    <>
      {isCompact ? (
        <ConversationInput
          {...inputProps}
          autoFocus
          // Explicit: ConversationInput defaults enableVoice to
          // `variant !== 'simple'`, so omitting it removes voice input.
          enableVoice
          variant='simple'
          sticky={false}
          compactUntilWrap
          // The hero composer carries its own surrounding space; the simple
          // variant has none, so without this inset it sits flush against the
          // viewport on a phone and reads like chrome rather than a control.
          className='mx-3 mb-3'
        />
      ) : (
        <HeroChatInput {...inputProps} autoFocus />
      )}

      {showDisclaimer && (
        <div
          className={cx(
            'max-w-3xl mx-auto px-3 sm:px-4 lg:px-6',
            // Closes the gap under the HERO box only; under the compact input
            // the negative margin would pull the text into the field.
            !isCompact && '-mt-[21px] sm:-mt-[13px]'
          )}
        >
          <p className='text-xs text-fg-quaternary text-center pt-1 pb-[7px]'>
            AI can make mistakes. Always double-check responses.
          </p>
        </div>
      )}
    </>
  );
}
